import React, { Component, ReactNode } from 'react';
import { TFunction } from 'i18next';
import { murmurhash } from '../../../../lib/js/hash';
import FormsRow from '../../../molecules/Form/components/FormsRow/FormsRow';
import Btn from '../../../molecules/Btn';
import LiveRegion from '../../../atoms/LiveRegion/LiveRegion';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import { createFormElement } from '../../../molecules/Form/FormFactory';
import FormsElementContainer, {
  FormsElementContainerModifier,
} from '../../../molecules/Form/components/FormsElementContainer/FormsElementContainer';

import { translateWithCustomTransMarker, tryTranslate } from '../../../../lib/translation/utils';
import { ContentParagraph } from '../../../../_lib/components/ContentParagraph';
import { IParameterProperty } from '../../../../types/IParameter';
import { RequiredHelpPhrase } from '../../../../_lib/Form2';

type GenericFormTemplateTypes = {
  inputs: IParameterProperty;
  status: InteractionStatus;
  statusMessages: Record<string, ReactNode | null>;
  hiddenFields?: ReactNode;
  hideHeadline?: boolean;
  hideFormHelpPhrase?: boolean;
  translationBaseKey: string;
  additions?: Record<string, any>;

  hasChanges: () => boolean;
  onChange: (event: Event) => void;
  getStatus: (statusMessages: Record<string, ReactNode>, status: InteractionStatus) => ReactNode;

  t: TFunction;
};

export default class GenericFormTemplate extends Component<GenericFormTemplateTypes> {
  static defaultProps = {
    hiddenFields: [],
    hideHeadline: false,
    hideFormHelpPhrase: false,
    additions: {},
  };

  private readonly idSuffix: string;

  constructor(props: GenericFormTemplateTypes) {
    super(props);
    this.idSuffix = murmurhash(this.props.translationBaseKey);
  }

  renderHeadLine() {
    return this.props.hideHeadline
      ? null
      : (
          <h2 className="form__headline">{this.props.t(`${this.props.translationBaseKey}.headline`)}</h2>
        );
  }

  renderFormHelpPhrase() {
    return this.props.hideFormHelpPhrase ? null : <RequiredHelpPhrase />;
  }

  renderHelpText() {
    const trans = tryTranslate(`${this.props.translationBaseKey}.helpText`);
    if (trans) {
      return (
        <FormsRow key={`${this.props.translationBaseKey}.helpText`}>
          <ContentParagraph>{trans as ReactNode}</ContentParagraph>
        </FormsRow>
      );
    }
    return null;
  }

  buttonDisabeld() {
    if (this.props.status === InteractionStatus.SUCCESS || this.props.status === InteractionStatus.SENDING) {
      return true;
    }

    const parameters = Object.values(this.props.inputs);

    if (parameters.length === 1 && parameters[0].options?.length === 1) {
      return false;
    }

    return !this.props.hasChanges();
  }

  render() {
    return (
      <>
        {this.renderHeadLine()}
        {this.renderHelpText()}
        {this.renderFormHelpPhrase()}
        {this.props.hiddenFields}
        {Object.values(this.props.inputs).map((parameter) => {
          return createFormElement(this.props.additions ? { ...this.props.additions[parameter.name] } : null)(
            parameter,
            this.idSuffix,
            this.props.onChange,
            translateWithCustomTransMarker(
              this.props.t(`${this.props.translationBaseKey}.inputs.${parameter.name}.label`),
            ),
          );
        })}

        <FormsRow>
          <FormsElementContainer modifier={FormsElementContainerModifier.BUTTON_GROUP_RIGHT}>
            <Btn
              cssClass="btn--single-action btn--single-action-yes forms__button"
              text={this.props.t(`${this.props.translationBaseKey}.button`)}
              type="submit"
              ariaDisabled={this.buttonDisabeld()}
            />
          </FormsElementContainer>
        </FormsRow>

        <LiveRegion htmlTag="div" invisible={false} ariaAtomic>
          {this.props.getStatus(this.props.statusMessages, this.props.status)}
        </LiveRegion>
      </>
    );
  }
}
